import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from '@mui/material'

import cn from 'classnames'

import styles from './styles.module.scss'
import documentStyles from '../document.module.scss'

import { confirm } from '../../../UI/Confirm/Confirm'
import CheckBox from '../../../UI/CheckBox/CheckBox'
import ItemsGroup from '../../ItemsGroup/ItemsGroup'
import ProcessingItemsGroup from '../../ProcessingItemsGroup/ProcessingItemsGroup'
import BasicDropzone from '../../BasicDropzone/BasicDropzone'
import LicenseForm from './LicenseForm/LicenseForm'
import Stub from './Stub/Stub'
import Spinner from '../../../UI/Spinner/Spinner'

import { getAuthMe, getListAuthLicenses } from '../../../api/getApi'
import { deleteProfileLicense, deleteSeafarerFile } from '../../../api/deleteApi'
import { getDocumentsItemGroupHelper } from '../../../helpers/itemsGroupHelper'
import { postProcessingDocument } from '../../../helpers/documents.helper'
import { getAuthLicensesHelper } from '../../../helpers/apiHelper'
import { useNotify } from '../../../hooks/useNotify'
import { useMobileSheetDragToClose } from '../../../hooks/useMobileSheetDragToClose'
import { deleteAuthDocument, deleteFileInDocument } from '../../../redux/actions/documents.action'
import { getUserInfo } from '../../../redux/actions/user.action'
import { fetchData, handleDeleteProcessingDocument } from '../utils'
import { documentType } from '../constants'

const LICENSES_ADD_EVENT = 'nextship:licenses:add'

const Licenses = () => {
  const [documentFiles, setDocumentFiles] = useState({
    license: [],
    endorsement: [],
  })
  const [currentDocumentId, setCurrentDocumentId] = useState(null)

  const isDesktop = useMediaQuery('(min-width:1025px)')
  const [isFormOpen, setIsFormOpen] = useState(false)

  const [isFetching, setIsFetching] = useState(false)
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false)
  const [fillFields, setFillFields] = useState(false)

  const formRef = useRef(null)
  const hasStartedFetchRef = useRef(false)

  const { processingDocuments, authDocuments } = useSelector(({ documents }) => documents)

  const dispatch = useDispatch()
  const { notify } = useNotify()

  const items = useMemo(() => {
    return getDocumentsItemGroupHelper(authDocuments, documentType.license)
  }, [authDocuments])

  useEffect(() => {
    fetchData(dispatch, setIsFetching, getListAuthLicenses, getAuthLicensesHelper, documentType.license)

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (isFetching) {
      hasStartedFetchRef.current = true
      return
    }

    if (hasStartedFetchRef.current) setIsInitialLoadDone(true)
  }, [isFetching])

  const handleFillFieldsClick = useCallback(() => {
    setFillFields((prev) => !prev)
  }, [])

  const handleEditDocument = useCallback(
    (id, currentDocument) => {
      if (currentDocument) {
        setCurrentDocumentId(id)
        if (!isDesktop) setIsFormOpen(true)
      } else {
        setCurrentDocumentId(null)
        if (!isDesktop) setIsFormOpen(false)
      }
    },
    [isDesktop]
  )

  const handleOpenAddForm = useCallback(() => {
    setCurrentDocumentId(null)
    setIsFormOpen(true)
  }, [])

  const closeFormImmediately = useCallback(() => {
    setCurrentDocumentId(null)
    setIsFormOpen(false)
  }, [])

  const {
    sheetOffsetY,
    isSheetClosing,
    isSheetDragging,
    requestCloseMobileSheet,
    handleSheetTransitionEnd,
    handleSheetHeaderPointerDown,
    handleSheetHeaderPointerMove,
    handleSheetHeaderPointerEnd,
  } = useMobileSheetDragToClose({
    isDesktop,
    isOpen: isFormOpen,
    onCloseImmediate: closeFormImmediately,
    ignoreSelector: `.${styles.controlToClose}`,
  })

  useEffect(() => {
    const onOpenAddFromTopbar = () => {
      if (isDesktop) return
      handleOpenAddForm()
    }

    window.addEventListener(LICENSES_ADD_EVENT, onOpenAddFromTopbar)
    return () => window.removeEventListener(LICENSES_ADD_EVENT, onOpenAddFromTopbar)
  }, [handleOpenAddForm, isDesktop])

  const handleDeleteDocument = useCallback(
    (id, currentDocument) => {
      confirm('Are you sure?', 'You want to delete this file?', async () => {
        try {
          await deleteProfileLicense(id)

          // reset form
          if (currentDocument) {
            setCurrentDocumentId(null)
          }

          if (authDocuments.length === 1) {
            const profile = await getAuthMe()

            dispatch(getUserInfo(profile))
          }

          dispatch(deleteAuthDocument(id))

          notify('Success', 'Your document was successfully deleted')
        } catch (error) {
          notify.errorsList(error.errors)
        }
      })
    },

    // eslint-disable-next-line
    [authDocuments.length]
  )

  const deleteDocumentFile = useCallback((fileId, documentId, type, currentDocument) => {
    confirm('Are you sure?', 'You want to delete this file?', async () => {
      setIsFetching(true)

      try {
        if (currentDocument) {
          await deleteSeafarerFile(fileId)

          dispatch(deleteFileInDocument(fileId, documentId, type))

          setDocumentFiles((prevState) => ({
            ...prevState,
            [type]: prevState[type].filter((file) => file.id !== fileId),
          }))

          notify('Your file was deleted from this document')

          return
        }

        await deleteSeafarerFile(fileId)

        dispatch(deleteFileInDocument(fileId, documentId, type))

        notify('Your file was deleted from this document')
      } catch (error) {
        notify.errorsList(error.errors)
      } finally {
        setIsFetching(false)
      }
    })

    // eslint-disable-next-line
  }, [])

  return (
    <div className={styles.documents_container}>
      {isDesktop && (
        <div className={styles.wrapper} ref={formRef}>
          {/* Processing section temporarily hidden
          {!fillFields && (
            <BasicDropzone type='processing_license' update={postProcessingDocument}>
              <p>
                Here you can add a photo of experience documents you have. Next Ship will fill in the input fields
                themselves according to the data
              </p>
            </BasicDropzone>
          )}
          */}

          <CheckBox onClick={handleFillFieldsClick} checked={fillFields}>
            Fill in the fields yourself
          </CheckBox>

          <LicenseForm
            currentDocumentId={currentDocumentId}
            setCurrentDocumentId={setCurrentDocumentId}
            documentFiles={documentFiles}
            setDocumentFiles={setDocumentFiles}
            fillFields={fillFields}
            setFillFields={setFillFields}
            authDocuments={authDocuments}
            onCloseForm={requestCloseMobileSheet}
          />
        </div>
      )}

      <div className={documentStyles.documents}>
        {isFetching && <Spinner className={documentStyles.documentsSpinner} />}

        {/* Processing section temporarily hidden
        {isDesktop && processingDocuments.length !== 0 && (
          <ProcessingItemsGroup
            type={documentType.license}
            items={processingDocuments}
            onDelete={handleDeleteProcessingDocument}
            currentId={currentDocumentId}
            count={processingDocuments.length}
            isLoading={isFetching}
          />
        )}
        */}

        {items.length !== 0 && (
          <ItemsGroup
            type={documentType.license}
            title='All licenses'
            items={items}
            onDelete={handleDeleteDocument}
            onEdit={handleEditDocument}
            deleteFile={deleteDocumentFile}
            currentId={currentDocumentId}
            count={authDocuments.length}
            isProcessingDocuments={processingDocuments.length}
            isFillFields={fillFields}
            isLoading={isFetching}
          />
        )}

        {!isFetching && isInitialLoadDone && items.length === 0 && processingDocuments.length === 0 && <Stub />}
      </div>

      {!isDesktop && isFormOpen && (
        <div
          className={cn(styles.mobileSheet, {
            [styles.mobileSheetDragging]: isSheetDragging,
            [styles.mobileSheetClosing]: isSheetClosing,
          })}
          style={
            sheetOffsetY === null
              ? undefined
              : {
                  transform: `translateY(${sheetOffsetY}px)`,
                }
          }
          onTransitionEnd={handleSheetTransitionEnd}
        >
          <div
            className={styles.mobileSheetHeader}
            onPointerDown={handleSheetHeaderPointerDown}
            onPointerMove={handleSheetHeaderPointerMove}
            onPointerUp={handleSheetHeaderPointerEnd}
            onPointerCancel={handleSheetHeaderPointerEnd}
          >
            <div className={styles.controlToClose} />

            <div className={styles.mobileSheetTitle}>Add License</div>
            <div className={styles.mobileSheetSubtitle}>Add your license details here.</div>
          </div>

          <div className={styles.mobileSheetContent} ref={formRef}>
            {/* Processing section temporarily hidden
            {!fillFields && (
              <BasicDropzone type='processing_license' update={postProcessingDocument}>
                <p>
                  Here you can add a photo of experience documents you have. Next Ship will fill in the input fields
                  themselves according to the data
                </p>
              </BasicDropzone>
            )}

            {!fillFields && !isDesktop && processingDocuments.length !== 0 && (
              <ProcessingItemsGroup
                type={documentType.license}
                items={processingDocuments}
                onDelete={handleDeleteProcessingDocument}
                currentId={currentDocumentId}
                count={processingDocuments.length}
                isLoading={isFetching}
              />
            )}
            */}

            <CheckBox onClick={handleFillFieldsClick} checked={fillFields}>
              Fill in the fields yourself
            </CheckBox>

            <LicenseForm
              currentDocumentId={currentDocumentId}
              setCurrentDocumentId={setCurrentDocumentId}
              documentFiles={documentFiles}
              setDocumentFiles={setDocumentFiles}
              fillFields={fillFields}
              setFillFields={setFillFields}
              authDocuments={authDocuments}
              onCloseForm={requestCloseMobileSheet}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default Licenses
